<template>
  <div :class="['check d-flex flex-column', { open: isOpen }]">
    <div
      class="d-flex align-items-center check-editor-header p-3"
      @click="isOpen = true"
    >
      <div class="d-flex flex-column">
        <h5 class="m-0">
          <inline-text-input
            v-if="isOpen && canEdit"
            :value="draftCheck.name"
            data-testid="check-name-input"
            placeholder="Document check"
            @input="draftCheck.name = $event.target.value"
          />
          <span v-else>{{ draftCheck.name || 'Document check' }}</span>
        </h5>
        <div class="d-flex align-items-center check-overview">
          <small v-if="draftCheck.triggers.length === 0"> All documents </small>
          <small v-else>
            {{ draftCheck.triggers.length }} trigger{{
              draftCheck.triggers.length !== 1 ? 's' : ''
            }}
          </small>
          <span class="mx-1">&bull;</span>
          <small>
            {{ draftCheck.requirements.length }} requirement{{
              draftCheck.requirements.length !== 1 ? 's' : ''
            }}
          </small>
          <span class="mx-1">&bull;</span>
          <div class="d-flex align-items-center">
            <small v-if="draftCheck.public">Show to users</small>
            <small v-else>Hide from users</small>
            <help-tooltip
              v-if="draftCheck.public"
              class="mt-1"
              :light="isOpen"
              text="If a document submitted during a collect session fails this check, the document will be rejected and the reasons for rejection will be presented to the customer"
            />
            <help-tooltip
              v-else
              class="mt-1"
              :light="isOpen"
              text="This check will not be shown to customers"
            />
          </div>
        </div>
      </div>
      <b-button
        v-if="isOpen"
        class="btn ml-auto cancel-button mr-4"
        variant="link"
        size="sm"
        @click.stop="() => onCancel()"
      >
        Cancel
      </b-button>
      <loading-button
        v-if="isOpen && canEdit"
        :loading="isLoading"
        class="btn delete-button btn-danger mr-2"
        size="sm"
        @click.stop="showDeleteConfirmationModal = true"
      >
        Delete
      </loading-button>
      <loading-button
        v-if="isOpen && canEdit"
        :loading="isLoading"
        class="btn save-button btn-secondary"
        size="sm"
        :disabled="!isValidCheck"
        @click.stop="() => onSave()"
      >
        Save
      </loading-button>
      <span v-if="!isOpen" class="open-icon ml-auto mr-3">
        <font-awesome-icon icon="caret-down" />
      </span>
    </div>
    <div v-if="isOpen" class="check-body p-3">
      <div class="d-flex align-items-center mb-2">
        <label>Document filter</label>
        <help-tooltip
          :light="isOpen"
          text="Add a filter to only apply this check to a subset of documents. To target all documents don't specify any filters"
        />
      </div>
      <div
        v-if="draftCheck.triggers.length === 0"
        class="p-2 mt-2 no-triggers d-flex align-items-center"
      >
        <div class="d-flex flex-column">
          <p>Apply to all documents</p>
          <small>
            Add a filter to limit which documents this check is applied to
          </small>
        </div>
        <b-button
          v-if="canEdit"
          class="btn ml-auto"
          variant="secondary"
          size="sm"
          @click="() => addAndCondition('triggers')"
        >
          Add filter
        </b-button>
      </div>
      <condition
        v-for="(condition, conditionIndex) in draftCheck.triggers"
        :key="`trigger-${conditionIndex}`"
        :condition="condition"
        :disabled="!canEdit"
        :is-last="conditionIndex === draftCheck.triggers.length - 1"
        :can-delete="canEdit"
        @update="
          (updatedCondition) =>
            onConditionUpdate('triggers', updatedCondition, conditionIndex)
        "
        @delete="() => onDeleteCondition('triggers', conditionIndex)"
      />
      <button
        v-if="triggersAreValid > 0 && canEdit"
        class="btn btn-secondary btn-sm"
        data-testid="new-condition-button"
        @click="() => addAndCondition('triggers')"
      >
        AND
      </button>
      <hr />
      <div class="d-flex align-items-center mb-2">
        <label>Check that the document matches these conditions</label>
        <help-tooltip
          :light="true"
          text="Specify the conditions that you want to check for in each of the qualifying documents"
        />
      </div>
      <condition
        v-for="(condition, conditionIndex) in draftCheck.requirements"
        :key="`requirement-${conditionIndex}`"
        :condition="condition"
        :disabled="!canEdit"
        :is-last="conditionIndex === draftCheck.requirements.length - 1"
        :can-delete="draftCheck.requirements.length > 1 && canEdit"
        @update="
          (updatedCondition) =>
            onConditionUpdate('requirements', updatedCondition, conditionIndex)
        "
        @delete="() => onDeleteCondition('requirements', conditionIndex)"
      />
      <button
        v-if="requirementsAreValid > 0 && canEdit"
        class="btn btn-secondary btn-sm"
        data-testid="new-condition-button"
        @click="() => addAndCondition('requirements')"
      >
        AND
      </button>
      <hr />
      <div class="d-flex ml-2 mt-4 mb-2">
        <b-form-checkbox
          v-model="draftCheck.public"
          class=""
          name="public-check-button"
          :disabled="!canBePublic"
          switch
        >
        </b-form-checkbox>
        <div class="d-flex flex-column ml-2 i-text-neutral-110">
          <span> Show this check to customers during collect sessions </span>
          <small>
            If enabled, documents submitted during a collect session that fail
            this check will be rejected and the reasons for rejection will be
            presented to the customer
          </small>
          <i-alert-message
            v-if="!canBePublic"
            class="i-mt-4"
            type="info"
            heading="Cannot display this check to customers"
            message="You cannot show this check to customers because of the variables you have selected"
          ></i-alert-message>
        </div>
      </div>
    </div>
    <b-modal
      id="team-invite-modal"
      ref="team-invite-modal"
      v-model="showDeleteConfirmationModal"
      header-bg-varian="danger"
      ok-variant="danger"
      title="Are you sure you want to delete this check?"
      ok-title="Yes, delete"
      @ok="() => onDelete()"
    >
      <p v-if="draftCheck.id">
        Deleting this check will stop flagging documents that fail to meet these
        conditions. The check will remain associated with any existing documents
        that it has been applied to.
      </p>
    </b-modal>
  </div>
</template>

<script>
import set from 'lodash/set';
import { InlineTextInput, LoadingButton, HelpTooltip } from '@inscribe/ui-kit';
import { iAlertMessage } from '@inscribe/signal';
import RuleEngine from '@/utils/ruleEngine';
import Condition from '@/V2/decision-engine/Condition';
import { PRIVATE_INTERNAL_CHECKS } from '@/constants/privateInternalChecks';

export default {
  components: {
    condition: Condition,
    'loading-button': LoadingButton,
    'inline-text-input': InlineTextInput,
    'help-tooltip': HelpTooltip,
    'i-alert-message': iAlertMessage,
  },
  props: {
    canEdit: {
      type: Boolean,
      required: true,
    },
    check: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isOpen: !this.check.id,
      isLoading: false,
      showDeleteConfirmationModal: false,
      conditionsChanged: false,
      draftCheck: JSON.parse(JSON.stringify(this.check)),
      canBePublic: null,
    };
  },
  computed: {
    triggersAreValid() {
      return (
        this.draftCheck.triggers.length > 0 &&
        this.draftCheck.triggers.every(
          (condition) =>
            !!condition.name && !!condition.operator && !!condition.value,
        )
      );
    },
    requirementsAreValid() {
      return (
        this.draftCheck.requirements.length > 0 &&
        this.draftCheck.requirements.every(
          (condition) =>
            !!condition.name && !!condition.operator && !!condition.value,
        )
      );
    },
    rulesSchema() {
      return this.$store.state.config.rulesSchema;
    },
    isValidCheck() {
      const ruleEngine = new RuleEngine(this.rulesSchema);
      return (
        this.rulesSchema &&
        !!this.draftCheck.name &&
        this.draftCheck.name.length > 2 &&
        ruleEngine.isValidConditionSet(this.draftCheck.triggers) &&
        this.draftCheck.requirements.length > 0 &&
        ruleEngine.isValidConditionSet(this.draftCheck.requirements)
      );
    },
  },
  mounted() {
    this.toggleCheckCanBePublic('requirements');
  },
  methods: {
    copyBaseCheck() {
      this.draftCheck = JSON.parse(JSON.stringify(this.check));
    },
    onCancel() {
      this.copyBaseCheck();
      this.isOpen = false;
      this.conditionsChanged = false;
    },
    onSave() {
      this.isLoading = true;
      const actionName = this.draftCheck.id ? 'updateCheck' : 'createCheck';

      const draftCheck = this.conditionsChanged
        ? this.draftCheck
        : {
            id: this.draftCheck.id,
            name: this.draftCheck.name,
            public: this.draftCheck.public,
          };

      this.$store
        .dispatch(actionName, draftCheck)
        .then((newCheck) => {
          this.isLoading = false;
          this.conditionsChanged = false;
          this.draftCheck = { ...newCheck };
          this.isOpen = false;

          const checkIndex = this.$store.state.config.checks.findIndex(
            (check) => check.id === this.check.id || check === this.check,
          );

          const updatedChecks = set(
            this.$store.state.config.checks,
            [checkIndex],
            newCheck,
          );
          this.$store.commit('setChecks', updatedChecks);
        })
        .catch(() => {
          this.isLoading = false;
          this.$notify({
            group: 'general',
            type: 'error',
            title: 'Error',
            duration: 5000,
            text: 'There was a problem saving your check. Please try again',
          });
        });
    },
    onDelete() {
      this.isLoading = true;
      const isDraft = !this.draftCheck.id;

      if (!isDraft) {
        this.$store.dispatch('deleteCheck', this.draftCheck.id).then(() => {
          this.isLoading = false;
          this.removeCheckFromStore();
        });
      } else {
        this.removeCheckFromStore();
      }
    },
    removeCheckFromStore() {
      const newChecks = this.$store.state.config.checks.filter(
        (check) => check.id !== this.check.id || check !== this.check,
      );

      this.$store.commit('setChecks', newChecks);
    },
    onConditionUpdate(type, updatedCondition, conditionIndex) {
      this.conditionsChanged = true;

      const newConditions = set(
        this.draftCheck[type],
        [conditionIndex],
        updatedCondition,
      );
      this.draftCheck = {
        ...this.draftCheck,
        [type]: newConditions,
      };

      this.toggleCheckCanBePublic(type);
    },
    onDeleteCondition(type, conditionIndex) {
      this.conditionsChanged = true;

      this.draftCheck = {
        ...this.draftCheck,
        [type]: this.draftCheck[type].filter(
          (__, index) => index !== conditionIndex,
        ),
      };

      this.toggleCheckCanBePublic(type);
    },
    addAndCondition(type) {
      this.conditionsChanged = true;

      this.draftCheck[type] = [
        ...this.draftCheck[type],
        {
          name: null,
          operator: null,
          value: null,
        },
      ];
    },
    toggleCheckCanBePublic(type) {
      this.canBePublic = true;

      this.draftCheck[type].forEach((condition) => {
        if (PRIVATE_INTERNAL_CHECKS.includes(condition.name)) {
          this.canBePublic = false;
          this.draftCheck.public = false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
.check {
  border: 1px solid $light-neutral-60;
  margin-bottom: 12px;
  border-radius: 16px;
  background-color: $light-neutral-default;

  .open-icon {
    display: none;
    color: $color-navy-blue-40;
    font-size: 24px;
  }

  .check-editor-header {
    border-radius: 16px 16px 0 0;
    position: sticky;
    top: 0;
    z-index: 25;

    h5 {
      font-size: $font-size-default;
    }

    .check-overview {
      color: $color-raven;
    }

    &:hover .open-icon {
      display: block;
    }
  }

  &.open .check-editor-header {
    background-color: $color-navy-blue-80;
    color: $color-white;

    .btn.btn-secondary.save-button:not(:disabled),
    .btn.btn-link.cancel-button:not(:disabled) {
      color: $color-white !important;
      opacity: 0.75;
      &:hover {
        opacity: 1;
      }
    }

    .check-overview {
      color: $color-raven-10;
    }

    .help-tooltip-wrapper .help-tooltip .help-icon {
      color: $color-white;
    }

    input {
      background-color: $color-navy-blue-80;
      color: $color-white;
    }

    input::placeholder {
      color: $color-white !important;
      opacity: 1;
    }

    .inline-text-input:hover input {
      background-color: $color-navy-blue-60 !important;
    }

    .btn.btn-secondary.save-button:disabled {
      cursor: not-allowed;
    }

    .btn.btn-secondary.save-button {
      border-color: $color-white !important;
    }
  }

  .no-triggers {
    border-radius: $border-radius-default;
    border: $border-width-input solid $color-raven-20;
  }
}
</style>
